import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';

import CopyCheckbox from './CopyCheckbox';
import CopyPlacementSelect from './CopyPlacementSelect';
import { buttonReset, focusRing } from '../../../../shared/theme/focusStyles';
import { FOCUSABLE_SELECTOR } from '../../../../shared/utils/focusHelpers';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const ITEM_TYPES_PANEL_ID = 'copy-item-types-panel';

const INCLUDE_OPTIONS = [
  { key: 'prescribedLoads', label: 'Prescribed loads' },
  { key: 'workoutTitles', label: 'Workout titles and colors' },
];

/** `group` says which state object the key lives in - the item types are
 * split across contentOptions and additionalOptions in the parent. */
const ITEM_TYPE_OPTIONS = [
  { key: 'circuits', group: 'content', label: 'Lifts' },
  { key: 'health', group: 'content', label: 'Tags' },
  { key: 'notes', group: 'content', label: 'SAQ+C' },
  { key: 'videos', group: 'content', label: 'Videos' },
  { key: 'rest', group: 'content', label: 'Rest' },
  { key: 'warmUps', group: 'content', label: 'Warm Ups' },
  { key: 'cooldowns', group: 'content', label: 'Cooldowns' },
  { key: 'questionnaires', group: 'content', label: 'Questionnaires' },
  { key: 'sessions', group: 'content', label: 'Sessions' },
  { key: 'circuits2', group: 'additional', label: 'Circuits' },
  { key: 'health2', group: 'additional', label: 'Health' },
  { key: 'notes2', group: 'additional', label: 'Notes' },
];

const OptionsContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GroupLabel = styled('div')`
  margin-bottom: 6px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const IncludeRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
`;

const PlacementRow = styled('div')`
  display: flex;
  gap: 24px;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const PlacementColumn = styled('div')`
  flex: 1;
  min-width: 0;
`;

const ItemTypesContainer = styled('div')`
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
`;

const HeaderButton = styled('button')`
  ${buttonReset}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 6px;
  cursor: pointer;
  ${focusRing}
`;

const HeaderTitle = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`;

const SummaryGroup = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

/* 6px + 16px + 6px padding and line height give the 28px pill height, and the
   14px radius is half of that so the ends come out fully round. */
const SummaryBadge = styled('span')`
  overflow: hidden;
  padding: 6px 10px;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled('span')`
  display: inline-flex;
  transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;
`;

/* `visibility` is what keeps the collapsed checkboxes out of the tab order -
   max-height alone leaves them focusable. It is delayed by the collapse
   duration so the animation still plays. */
const ItemTypesPanel = styled('div')`
  overflow: hidden;
  max-height: ${({ expanded }) => (expanded ? '1000px' : '0px')};
  visibility: ${({ expanded }) => (expanded ? 'visible' : 'hidden')};
  transition: max-height 0.3s ease,
    visibility 0s linear ${({ expanded }) => (expanded ? '0s' : '0.3s')};
`;

const ItemTypesGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2px 8px;
  padding: 0 16px 16px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const buildItemTypesSummary = (contentOptions, additionalOptions) => {
  const checkedCount = ITEM_TYPE_OPTIONS.filter((option) => (
    option.group === 'content' ? contentOptions[option.key] : additionalOptions[option.key]
  )).length;
  if (checkedCount === ITEM_TYPE_OPTIONS.length) {
    return 'All selected';
  }
  if (checkedCount === 0) {
    return 'None selected';
  }
  return `${checkedCount} of ${ITEM_TYPE_OPTIONS.length} selected`;
};

const CopyOptionsSection = ({
  showItemTypes,
  contentOptions,
  additionalOptions,
  onToggleContentOption,
  onToggleAdditionalOption,
  placementOptions,
  placementOption,
  onPlacementChange,
  clearExistingWorkouts,
  onToggleClearExisting,
}) => {
  const [isItemTypesExpanded, setIsItemTypesExpanded] = useState(false);
  const [focusPanelRequest, setFocusPanelRequest] = useState(0);
  const headerButtonRef = useRef(null);
  const { colors } = useTheme();

  /* Expanding moves focus into the panel; collapsing hands it back to the
     header, which would otherwise be lost when the panel becomes hidden. */
  const handleItemTypesHeaderClick = () => {
    const willExpand = !isItemTypesExpanded;
    setIsItemTypesExpanded(willExpand);
    if (willExpand) {
      setFocusPanelRequest((count) => count + 1);
    } else {
      headerButtonRef.current?.focus();
    }
  };

  const handleIncludeChange = (e) => {
    onToggleContentOption(e.target.dataset.key);
  };

  const handleItemTypeChange = (e) => {
    const { key, group } = e.target.dataset;
    if (group === 'content') {
      onToggleContentOption(key);
    } else {
      onToggleAdditionalOption(key);
    }
  };

  useEffect(() => {
    if (!focusPanelRequest) {
      return;
    }
    document.getElementById(ITEM_TYPES_PANEL_ID)?.querySelector(FOCUSABLE_SELECTOR)?.focus();
  }, [focusPanelRequest]);

  return (
    <OptionsContent>
      <div>
        <GroupLabel>Include</GroupLabel>
        <IncludeRow>
          {INCLUDE_OPTIONS.map((option) => (
            <CopyCheckbox
              checked={!!contentOptions[option.key]}
              id={`copy-option-${option.key}`}
              inputProps={{ 'data-key': option.key }}
              key={option.key}
              label={option.label}
              onChange={handleIncludeChange}
            />
          ))}
        </IncludeRow>
      </div>

      <PlacementRow>
        <PlacementColumn>
          <GroupLabel as='label' htmlFor='copy-option-placement'>Placement</GroupLabel>
          <CopyPlacementSelect
            inputId='copy-option-placement'
            onChange={onPlacementChange}
            options={placementOptions}
            value={placementOption}
          />
        </PlacementColumn>
        <PlacementColumn>
          <GroupLabel>Existing workouts</GroupLabel>
          <CopyCheckbox
            checked={clearExistingWorkouts}
            id='copy-option-clear-existing'
            label='Clear existing workouts'
            onChange={onToggleClearExisting}
          />
        </PlacementColumn>
      </PlacementRow>

      {showItemTypes && (
        <ItemTypesContainer>
          <HeaderButton
            aria-controls={ITEM_TYPES_PANEL_ID}
            aria-expanded={isItemTypesExpanded}
            onClick={handleItemTypesHeaderClick}
            ref={headerButtonRef}
            type='button'
          >
            <HeaderTitle>Item types</HeaderTitle>
            <SummaryGroup>
              <SummaryBadge data-testid='copy-item-types-summary'>
                {buildItemTypesSummary(contentOptions, additionalOptions)}
              </SummaryBadge>
              <Chevron expanded={isItemTypesExpanded}>
                <IcomoonReact aria-hidden='true' color={colors.textSecondary} icon='down-arrow' iconSet={iconSet} size={10} />
              </Chevron>
            </SummaryGroup>
          </HeaderButton>

          <ItemTypesPanel expanded={isItemTypesExpanded} id={ITEM_TYPES_PANEL_ID}>
            <ItemTypesGrid>
              {ITEM_TYPE_OPTIONS.map((option) => (
                <CopyCheckbox
                  checked={!!(option.group === 'content'
                    ? contentOptions[option.key]
                    : additionalOptions[option.key])}
                  id={`copy-option-${option.key}`}
                  inputProps={{ 'data-key': option.key, 'data-group': option.group }}
                  key={option.key}
                  label={option.label}
                  onChange={handleItemTypeChange}
                />
              ))}
            </ItemTypesGrid>
          </ItemTypesPanel>
        </ItemTypesContainer>
      )}
    </OptionsContent>
  );
};

CopyOptionsSection.propTypes = {
  showItemTypes: PropTypes.bool.isRequired,
  contentOptions: PropTypes.objectOf(PropTypes.bool).isRequired,
  additionalOptions: PropTypes.objectOf(PropTypes.bool).isRequired,
  onToggleContentOption: PropTypes.func.isRequired,
  onToggleAdditionalOption: PropTypes.func.isRequired,
  placementOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  placementOption: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }).isRequired,
  onPlacementChange: PropTypes.func.isRequired,
  clearExistingWorkouts: PropTypes.bool.isRequired,
  onToggleClearExisting: PropTypes.func.isRequired,
};

export default CopyOptionsSection;
